import { css } from '@emotion/react';
import { Link, LinkProps } from './link';
import { Button, ButtonProps } from './button';
import { IconNameTypes, Icon } from '../foundations/icon';
import { ExportableAdgColorTypes } from '../foundations/adg-colors';
import { createElement } from 'react';
import { Image, AspectRatios } from './image';
import { getMediaDown } from '../foundations/breakpoints';

export type HeaderTagTypes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | undefined;
export type OrientationTypes = 'horizontal' | 'vertical';

export interface ButtonAndLinkProps {
  buttonProps: ButtonProps;
  primaryLinkProps: LinkProps;
}

export interface OneLinkProps {
  primaryLinkProps: LinkProps;
}

export interface OneButtonProps {
  buttonProps: ButtonProps;
}

export interface TwoLinksProps {
  primaryLinkProps: LinkProps;
  secondaryLinkProps: LinkProps;
}

export interface EyebrowProps {
  eyebrowText: string;
}

export type CTAProps =
  | OneButtonProps
  | OneLinkProps
  | ButtonAndLinkProps
  | TwoLinksProps;

export interface ContentBlockProps {
  headerText: string;
  bodyText?: string;
  headerType?: HeaderTagTypes;
  footerHeader?: string;
  footerText?: string;
  imageLink?: string;
  altText?: string;
  decoration?: string;
  eyebrowText?: string;
  iconName?: IconNameTypes;
  iconColor?: ExportableAdgColorTypes;
  className?: string;
  ctaProps?: CTAProps;
  isCentered?: boolean;
  cardStyle?: boolean;
  aspectRatio?: AspectRatios;
  orientation?: OrientationTypes;
}

function getHeaderTag(text: string, header: HeaderTagTypes) {
  const headerTag = header ? header : 'h3';
  return createElement(headerTag, null, text);
}

/* Gives h tag one less than header */
function getEyebrowTag(
  headerType: HeaderTagTypes | undefined,
  text: string | undefined
) {
  const tagNumber = headerType ? Number(headerType.slice(-1)) : 5;
  return createElement(
    `h${tagNumber + 1}`,
    { className: 'contentBlock-eyebrow' },
    text
  );
}

function renderLinks(ctaProps?: CTAProps) {
  if (!ctaProps) {
    return null;
  }
  let firstLink;
  if ('buttonProps' in ctaProps) {
    firstLink = <Button {...ctaProps.buttonProps} />;
  } else if ('primaryLinkProps' in ctaProps) {
    firstLink = <Link {...ctaProps.primaryLinkProps} />;
  }
  const secondaryLink =
    'secondaryLinkProps' in ctaProps ? (
      <Link {...ctaProps.secondaryLinkProps} />
    ) : null;
  return (
    <div className="contentBlock-linkFlex">
      {firstLink}
      {secondaryLink}
    </div>
  );
}

/**
 * This method retrieves either the eyebrow or icon decoration. If both are present, the eyebrow is returned.
 * @param headerType
 */
const getEyebrowOrIcon = (
  headerType?: HeaderTagTypes,
  eyebrowText?: string,
  iconName?: IconNameTypes,
  iconColor?: ExportableAdgColorTypes
) => {
  if (iconName) {
    return <Icon name={iconName} color={iconColor} />;
  }
  return getEyebrowTag(headerType, eyebrowText);
};

const containerStyle = (
  isCentered: boolean,
  direction?: string,
  cardStyle?: boolean
) => css`
  height: 100%;
  ${direction == 'horizontal'
    ? `
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    .img-section {
      img {
        height: 100%;
      }
    }
    ${getMediaDown('sm', true)} {
      grid-template-columns: minmax(0, 1fr);
    }

  `
    : `
    display: flex;
    flex-direction: column;
  `};

  .img-section {
    line-height: 1;
    display: block;
    img {
      width: 100%;
    }
  }

  .text-section {
    ${cardStyle ? 'padding: 20px;' : ''}
    text-align: ${isCentered ? 'center' : 'left'};
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    & > * {
      margin: 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 600;
  }

  a,
  button {
    margin-top: 0.5rem;
    width: fit-content;
  }

  .contentBlock-body {
    font-weight: 400;
  }

  .contentBlock-eyebrow {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
    order: -1;
  }

  .icon {
    order: -1;
  }

  .contentBlock-linkFlex {
    display: flex;
    flex-wrap: wrap;
    gap: 10%;
    justify-content: ${isCentered ? 'center' : 'flex-start'};
    align-items: center;
  }
`;

export const ContentBlock = ({
  headerText,
  bodyText,
  headerType = 'h3',
  eyebrowText,
  className,
  imageLink,
  altText,
  iconName,
  iconColor,
  ctaProps,
  aspectRatio = '1/1',
  orientation,
  cardStyle,
  isCentered = false
}: ContentBlockProps) => {
  if (imageLink && !altText) {
    throw new Error(
      'If using an image, please provide an alt text for accessibility purposes!'
    );
  }
  return (
    <div
      className={`content-block ${className}`}
      css={containerStyle(isCentered, orientation, cardStyle)}
    >
      {/* Header is first in HTML for accessibility but order is changed visually through CSS */}
      {imageLink && altText ? (
        <div className="img-section">
          <Image src={imageLink} alt={altText} aspectRatio={aspectRatio} />
        </div>
      ) : null}

      <div className="text-section">
        {headerText && getHeaderTag(headerText, headerType)}

        {getEyebrowOrIcon(headerType, eyebrowText, iconName, iconColor)}

        {bodyText && <p className="contentBlock-body">{bodyText}</p>}

        {renderLinks(ctaProps)}
      </div>
    </div>
  );
};
